import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    AbstractControl,
    ValidationErrors,
    Validator,
    ValidatorFn,
} from '@angular/forms';
import { NgbDateAdapter, NgbDateStruct } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import {
    addDays,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
} from 'date-fns';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import {
    FocusSelectionParams,
    GoalThreshold,
} from '../../coaching-base/config';
import {
    CoachingDiffDecorateConnector,
    CoachingDiffDecoratorService,
    DiffItem,
} from '../../coaching-base/diff-decorator';
import {
    CoachableMetric,
    CommitmentKpi,
    EntityType,
    FocusDto,
    GoalModel,
    GoalStatus,
} from '../../coaching-base/general';
import { determineDaysToAchievement } from '../../coaching-detail/pillar-two/commitment-summary.transformer';
import { GraphDefinition } from '../../coaching-insight/line-area.component';
import {
    GraphType,
    Metric,
    MetricDetailGridObservables,
    Opportunity,
    PerformanceData,
    RawGraphData,
} from '../../coaching-insight/oa/models/oa.model';
import { TeamOAService } from '../../coaching-insight/oa/services/team-oa.service';
import {
    CmpsForm,
    CmpsFormBuilder,
} from '../../coaching-observation/experimental.form';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { CacheDateItem } from '../../insight/performance-data/cache-date.pd.model';
import {
    CriteriaIdType,
    PerformanceDataViewType,
} from '../../insight/performance-data/metric.pd.model';
import { PerformanceDataService } from '../../insight/performance-data/performance-data.service';
import { StateToProps } from '../../store';
import { disuseStore, useDispatcher, useStore } from '../../store.decorator';
import { PeriodType } from '../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import {
    CloseGoalCommitmentOption,
    CloseGoalModalCommitmentComponent,
} from '../commitment-close-goal/close-goal-modal.commitment.component';
import {
    goalCommitmentInit,
    goalCommitmentRetrieveData,
    goalCommitmentRetrieveDataCancel,
} from '../commitment-goals/goals.commitment.action';
import {
    CommitmentCloseGoalValue,
    GoalFormFields,
    GoalFormValue,
    goalFormValueTransformer,
} from '../commitment-goals/goals.commitment.form';
import goalsReducer from '../commitment-goals/goals.commitment.reducer';
import { GoalCommitmentState } from '../commitment-goals/goals.commitment.state';
import { ObservationFormValue } from '../commitment-observations/observations.commitment.form';
import { SharedGoalsCommitmentComponent } from '../commitment-shared-goals/shared.goals.commitment.component';
import { GoalService } from '../goal/goal.service';
import { CommitmentGoalHistory } from '../models/commitment-goal-history.model';
import { datesAreEqual } from '../models/commitment.model';

const CoachingDateToday = new Date();

const generateEmptyStateData = (): any[] => {
    const r = [0, 1, 2, 3, 4, 5, 6];
    return r.map((a, idx) => {
        return {
            target: 0,
            actual: 3,
            eventDate: addDays(new Date(), a),
        };
    });
};

@Component({
    selector: 'cmps-goal-detail-commitment',
    templateUrl: './detail.goal.commitment.component.html',
    host: {
        class: 'cmps-goal-detail-commitment',
    },
    providers: [DateProviders],
})
@TakeUntilDestroy()
export class GoalDetailCommitmentComponent
    implements
        OnChanges,
        OnInit,
        OnDestroy,
        Validator,
        StateToProps<GoalCommitmentState>,
        CoachingDiffDecorateConnector
{
    state: GoalCommitmentState;
    @Input() storePathPrefix: string;

    @Input() goal: GoalFormValue;
    @Input() updateParentGoalMetricData: (value: GoalFormValue) => void;
    @Input() commitmentId = 0;
    @Input() focus: FocusDto;
    @Input() performerId: number;
    @Input() linkedObservations: ObservationFormValue[];
    @Input() openDetailAsEdit: boolean;
    @Input() hideEditButton: boolean = false;
    @Input() hideCloseDeleteButton: boolean;
    @Input() openGoalDetailsInEditMode = false;
    @Input() selectedGoalItemId: number;
    @Input() goalsFromOtherCommitments: CommitmentKpi[];
    @Input() isSelectedGoalShared: boolean;
    @Input() commitmentKpis: CommitmentKpi[];
    @Input() isGoalFromOtherCommitment = false;
    @Input() disabled: boolean;

    /** generic button was clicked in pillar 3 or not */
    @Input() isGeneric: boolean;

    /** the selected metric. usually from pillar 1 */
    @Input() activeMetricId: number;

    /** @deprecated */
    @Input() isOriginalWithDraftTrackingIdMap: {
        [trackingId: number]: boolean;
    };

    @Input() goalThresholds: GoalThreshold[];

    @Input() focusSelectionParams: FocusSelectionParams;

    @Output() onSaveGoalClick: EventEmitter<GoalFormValue>;
    @Output() onGoalHistoryClick: EventEmitter<boolean>;
    @Output() onSelectedMetric: EventEmitter<GoalFormValue>;
    @Output() onUpdateGoalStatusClick: EventEmitter<GoalFormValue>;
    @Output() onGoalCloseClick: EventEmitter<CommitmentCloseGoalValue>;
    /** @private */ _i18n = TranslationKeyMap; // this breaks convention
    /** @private */ _form: CmpsForm<GoalFormFields>;
    /** @private */ _metricOpts: IOption[];
    /** @private */ _minDateStruct: NgbDateStruct;
    /** @private */ _selected: GoalFormValue;
    /** @private */ _graphSet: GraphDefinition[];
    /** @private */ _GoalStatus = GoalStatus;

    private readonly _onChange: (_: any) => void;
    private readonly _onTouched: () => void;
    private _onValidatorChange: () => void;
    private readonly _periodTypeChanged: Subject<PeriodType>;

    /** @private */ _reviewingSharedGoal = false;
    /** @private */ _reviewingUpdatedSharedGoal = false;
    /** @private */ _localPeriodType: PeriodType;

    // Left side panel

    /** @private */
    _daysToAchievement: {
        daysToAchievementIcon: string;
        daysToAchievementColor: string;
        daysToAchievementLabel: string;
        daysToAchievement: number;
        daysActive: number;
    };

    initialActual: number = null;
    goalCreator: string;
    oppActiveDays: number;

    _isEditing = false;
    isEnableSnackbar = false;
    selectedGoalId: any;
    goalsActives: any;
    daterange: PeriodType = PeriodType.ROLLING_30;
    gridPeriods: any[] = null;
    metricnames: string[] = [];
    actualGridData: PerformanceData[] = null;
    componentLength: number;

    actualVarianceData: RawGraphData[];
    actualTrendData: RawGraphData[];
    componentData: RawGraphData[];
    activeSubTab: GraphType = GraphType.TREND;

    startInitTrend = false;
    startInitVariance = false;
    startInitGrid = false;

    activeMetric: Opportunity = {
        metric: {},
    };

    PeriodType = PeriodType;
    GraphType = GraphType;

    calculatedGoalOrImprovement = false;
    showEmptyGraph = false;

    topdate: Date;
    isLoadingMetricPerformanceData = false;

    sgData: any[] = [];

    i18n = TranslationKeyMap;

    defaultExpectedDateIncrement = 30;

    /** @private */ get _value(): GoalFormValue {
        return this._form.form.value;
    }

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _goalService: GoalService,
        private readonly _teamOAService: TeamOAService,
        private readonly _piService: PerformanceDataService,
        private readonly _ngbDateAdapter: NgbDateAdapter<Date>,
        private readonly _translateService: TranslateService,
        private readonly _snackBar: CmpsSnackBar,
        private readonly _dialog: CmpsDialog,
        private readonly _diffDecoratorService: CoachingDiffDecoratorService,
    ) {
        this.disabled = false;
        this.isOriginalWithDraftTrackingIdMap = {};

        this.onSaveGoalClick = new EventEmitter<GoalFormValue>();
        this.onGoalHistoryClick = new EventEmitter<boolean>();
        this.onSelectedMetric = new EventEmitter<GoalFormValue>();
        this.onUpdateGoalStatusClick = new EventEmitter<GoalFormValue>();
        this.onGoalCloseClick = new EventEmitter<CommitmentCloseGoalValue>();

        this._localPeriodType = PeriodType.ROLLING_30;
        this._periodTypeChanged = new Subject();
        this._periodTypeChanged.pipe(takeUntilDestroy(this)).subscribe({
            next: (value) => {
                this._localPeriodType = value;
                if (this._selected.metric) {
                    this._reloadData(this._selected.metric);
                }
            },
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(changes: SimpleChanges) {
        // TODO FIXME refactor so that this can be called from ngOnInit
        if (!(this as any)._storePath) {
            useStore(
                this,
                `${this.storePathPrefix}.goalCommitment`,
                goalsReducer.goalCommitment,
            );
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('goal')) {
            this._reviewingSharedGoal = false;

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            this._selected = {
                goalId: null,
                tracking_id: Math.random(),
                date: addDays(new Date(), this.defaultExpectedDateIncrement),
            } as GoalFormValue;

            // happens when loaded goal (or FocusKPI)
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this.goal && this.goal.metric) {
                this.activeMetric = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    metric: {
                        metricId: this.goal.metric.toString(),
                        metricName: this.goal.metricName,
                    } as Metric,
                };

                this._selected = { ...this.goal };

                let useMinDate = new Date();

                // default the date to today + 30 days
                // if there is no date existing, prevent setting prior to today
                if (!this._selected.date) {
                    this._selected.date = addDays(new Date(), 30);
                } else {
                    // if there is date existing, prevent setting prior to existing date
                    if (this._selected.goalId) {
                        useMinDate = this._selected.date;
                    }
                }

                this._minDateStruct =
                    this._ngbDateAdapter.fromModel(useMinDate);

                this._setDaysToAchievement(
                    addDays(
                        new Date(),
                        this._selected.daysToAchievement != null
                            ? this._selected.daysToAchievement
                            : 0,
                    ),
                );

                this.initForm();
                this._patchForm();
                this.show_snackbar();
                this._reloadData(this._selected.metric);
            } else {
                this._graphSet = null;
                this.actualVarianceData = [];
                this.actualGridData = [];
                this.actualTrendData = [];
                useDispatcher(this, goalCommitmentRetrieveDataCancel({}));

                this.initForm();
                this._patchForm();
                this.show_snackbar();

                this._resetGrid();

                this.activeMetric = {
                    metric: {},
                };

                this._setDaysToAchievement(
                    addDays(new Date(), this.defaultExpectedDateIncrement),
                ); // default 30 days
                this._minDateStruct = this._ngbDateAdapter.fromModel(
                    new Date(),
                );
            }

            // TODO consolidate
            this._ngZone.onStable.pipe(take(1)).subscribe({
                complete: () => this._diffDecoratorService.redecorate(this),
            });
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('openGoalDetailsInEditMode')) {
            this._isEditing = this.openGoalDetailsInEditMode;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('disabled')) {
            this._setDisabledState(this.disabled);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initForm() {
        if (!this._form) {
            this._form =
                this._formBuilder.create<GoalFormFields>(GoalFormFields);
            this._form.setValidators(this._form.fields.GOAL, null, [
                {
                    fn:
                        (form): ValidatorFn =>
                        (c: AbstractControl): ValidationErrors | null => {
                            const other =
                                form.controls[
                                    this._form.fields.IMPROVEMENT.name
                                ];
                            if (
                                c.value == null &&
                                (other.value == null || !other.valid)
                            ) {
                                return { requiredWhen: {} };
                            }
                            return null;
                        },
                },
            ]);
            this._form.setValidators(this._form.fields.IMPROVEMENT, null, [
                {
                    fn:
                        (form): ValidatorFn =>
                        (c: AbstractControl): ValidationErrors | null => {
                            const other =
                                form.controls[this._form.fields.GOAL.name];
                            if (
                                c.value == null &&
                                (other.value == null || !other.valid)
                            ) {
                                return { requiredWhen: {} };
                            }
                            return null;
                        },
                },
            ]);

            this._form.form.controls[this._form.fields.METRIC.name].valueChanges
                .pipe(
                    filter((value) => value != null),
                    switchMap((value) => {
                        this._reviewingSharedGoal = false;

                        const metric = this.state.metrics
                            ? this.state.metrics.find(
                                  (s) => s.metricId === value,
                              )
                            : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                              ({} as CoachableMetric);

                        this.activeMetric = {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                            metric: {
                                metricId: String(metric.metricId), // TODO FIXME
                                metricName: metric.metricName,
                            } as Metric,
                        };

                        this._selected.metricName = metric.metricName;
                        this._selected.metric = metric.metricId;
                        this.onSelectedMetric.emit({ ...this._selected });

                        // TODO: move logic to state
                        return this._goalService
                            .getActiveGoalMetrics(
                                value.toString(),
                                this.performerId.toString(),
                            )
                            .pipe(
                                switchMap((goals: GoalModel[]) => {
                                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                    if (goals && goals.length) {
                                        return this._sharedGoalModalCreate().pipe(
                                            switchMap((ret) => {
                                                let v = value;

                                                if (ret) {
                                                    const g =
                                                        goalFormValueTransformer(
                                                            goals[0],
                                                        );
                                                    g.shared = true;

                                                    const m =
                                                        this.state.metrics.find(
                                                            (s) =>
                                                                s.metricId ===
                                                                g.metric,
                                                        );
                                                    this._selected = {
                                                        ...g,
                                                        associateToParentFocus:
                                                            true,
                                                        metricName: m
                                                            ? m.metricName
                                                            : '',
                                                    };
                                                    this._reviewingSharedGoal =
                                                        true;
                                                } else {
                                                    v = null;
                                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                                                    this._selected = {
                                                        goalId: null,
                                                        tracking_id:
                                                            Math.random(),
                                                    } as GoalFormValue;
                                                    this._resetChart();
                                                    this._resetGrid();
                                                    this.activeSubTab =
                                                        GraphType.TREND;
                                                }

                                                this._patchForm();

                                                return of(v);
                                            }),
                                        );
                                    } else {
                                        return of(value);
                                    }
                                }),
                                takeUntilDestroy(this),
                            );
                    }),
                    tap((s) => {
                        if (s && !this.isLoadingMetricPerformanceData) {
                            this._reloadData(s);
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();

            this._form.form.controls[this._form.fields.GOAL.name].valueChanges
                .pipe(
                    tap((value) => {
                        this._patchFormDataDependent(
                            this._form.form.controls[
                                this._form.fields.IMPROVEMENT.name
                            ],
                            () => this._calcImprovementFromGoal(value),
                        );
                    }),
                    debounceTime(300),
                    tap(() => {
                        if (this._selected.goal && this._selected.date) {
                            this._initchart();
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();

            this._form.form.controls[
                this._form.fields.IMPROVEMENT.name
            ].valueChanges
                .pipe(
                    tap((value) => {
                        this._patchFormDataDependent(
                            this._form.form.controls[
                                this._form.fields.GOAL.name
                            ],
                            () => this._calcGoalFromImprovement(value),
                        );
                        // Load start percentage to goal when improvement changes
                        // this.getStartPercentageToGoal()
                    }),
                    debounceTime(500),
                    tap(() => {
                        if (
                            this._form.form.controls[
                                this._form.fields.GOAL.name
                            ].value &&
                            this._form.form.controls[
                                this._form.fields.DATE.name
                            ].value
                        ) {
                            this._initchart();
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();

            this._form.form.controls[this._form.fields.DATE.name].valueChanges
                .pipe(
                    debounceTime(500),
                    filter<Date>((value) => this._dateIsValid(value)),
                    tap((value) => {
                        this._setDaysToAchievement(
                            this._form.form.controls[
                                this._form.fields.DATE.name
                            ].value,
                        );
                        if (
                            this._form.form.controls[
                                this._form.fields.GOAL.name
                            ].value &&
                            value
                        ) {
                            this._initchart();
                        }
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();

            // this._minDate = this._ngbDateAdapter.fromModel(new Date());

            useDispatcher(
                this,
                goalCommitmentInit({
                    performerId: this.performerId,
                    focusSelectionParams: this.focusSelectionParams,
                }),
            );
        }
    }

    ngOnInit(): void {
        this._diffDecoratorService.registerForEntityType(EntityType.Goal, this);

        this.initForm();
    }

    ngOnDestroy(): void {
        disuseStore(this);

        this._diffDecoratorService.unRegisterForEntityType(
            EntityType.Goal,
            this,
        );

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.form.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
        });
        this._form.checkWarningsErrors();
        return this._form.form.errors;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stateToProps(prevState?: GoalCommitmentState) {
        if (!prevState) {
            return;
        }

        if (prevState.pending.init && !this.state.pending.init) {
            this._metricOpts = (
                this.state.metrics || ([] as CoachableMetric[])
            ).map((e) => ({
                label: e.metricName,
                value: e.metricId,
            }));

            // TODO consolidate
            this._ngZone.onStable.pipe(take(1)).subscribe({
                complete: () => this._diffDecoratorService.redecorate(this),
            });
        }

        if (prevState.pending.data && !this.state.pending.data) {
            // add in current actual to lookup
            this._assignMetricDataAndRedrawChart();
        }

        if (prevState.pending.history && !this.state.pending.history) {
            this._decideChartRedraw();
        }

        if (prevState.pending.events && !this.state.pending.events) {
            this._decideChartRedraw();
        }
    }

    ddcDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        ) as HTMLElement;
        if (!el) {
            return;
        }
        el.classList.add(`cmps-goal-detail-commitment--dd-goal-${state}`);
    }

    ddcUnDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            '[data-diffitemid]',
        ) as HTMLElement;
        if (!el) {
            return;
        }
        el.classList.remove(`cmps-goal-detail-commitment--dd-goal-${state}`);
    }

    _decideChartRedraw(): void {
        if (this.state.metricData) {
            this._initchart();
        } else {
            this._resetChart();
        }
    }

    _assignMetricDataAndRedrawChart(): void {
        this._selected.actual = this.state.lastMetricDataItem?.actual;
        this._selected.rankOrder =
            this.state.lastMetricDataItem?.rankOrder === 0 ? -1 : 1;
        this._selected.target = this.state.lastMetricDataItem?.target;
        this._selected.percentToTarget =
            this.state.lastMetricDataItem?.percentToTarget;

        /* assume it's new and injected. update the parent data */
        if (this._selected.goalId == null) {
            this.updateParentGoalMetricData?.(this._selected);
        }

        this._patchForm();

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._selected &&
            this._selected.goal &&
            !this.calculatedGoalOrImprovement
        ) {
            this._patchFormDataDependentLazy();
        }

        this._decideChartRedraw();

        this.isLoadingMetricPerformanceData = false;
    }

    /** @private */ _onPeriodTypeClick(e: PeriodType): void {
        this._periodTypeChanged.next(e);
    }

    /** @private */ _onCancelGoalReview(): void {
        this._reviewingSharedGoal = false;
        this._selected = null;
        this._form.form.reset({
            metric: this._form.form.get(this._form.fields.METRIC.name).value,
        });
        this.onSelectedMetric.emit(this._selected);
    }

    /** @private */ _onGoalHistoryClick(): void {
        this.onGoalHistoryClick.emit(true);
    }

    /** @private */ _onSaveGoalClick(): void {
        const isValid = (): boolean => {
            if (!this._value.associateToParentFocus) {
                return this._form.form.valid;
            } else {
                return (
                    this._form.form.errors == null &&
                    this._dateIsValid(this._value.date)
                );
            }
        };

        this._form.checkWarningsErrors();
        if (!isValid()) {
            return;
        }

        const goal = this._form.form.value as GoalFormValue;
        const metric =
            this.state.metrics.find((a) => a.metricId === goal.metric) ||
            // TODO revise. this is related to existing goal metrics that are not part of the performer's current coachable metrics
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            ({ metricName: goal.metricName } as CoachableMetric);

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const nextGoal = {
            ...goal,
            actual: this._selected.actual,
            initialActual: this._selected.initialActual,
            target: this._selected.target,
            percentToTarget: this._selected.percentToTarget,
            metricName: metric.metricName,
            associateToParentFocus: this._selected.associateToParentFocus,
            shared: this._selected.shared,
            daysToAchievement: this._daysToAchievement
                ? this._daysToAchievement.daysToAchievement
                : 0,
            statusId: goal.statusId != null ? goal.statusId : GoalStatus.DRAFT,
        } as GoalFormValue;

        // trigger a new draft-update to be added
        if (nextGoal.isOriginalGoal) {
            nextGoal.isOriginalGoal = undefined;
            nextGoal.diffItemId = undefined;
            nextGoal.tracking_id = Math.random();
        }

        this.onSaveGoalClick.emit(nextGoal);

        this._selected = { ...this._selected, ...nextGoal };

        this._isEditing = false;
        useDispatcher(this, goalCommitmentRetrieveDataCancel({}));

        this._patchForm();

        this._reviewingSharedGoal = false;
    }

    /** @private */ _onDeleteClick(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9498_DO_YOU_WANT_TO_DELETE_THIS_DRAFT_GOAL_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        const goal = this._form.form.value as GoalFormValue;
                        this.onUpdateGoalStatusClick.emit({ ...goal });
                        useDispatcher(
                            this,
                            goalCommitmentRetrieveDataCancel({}),
                        );
                        this._patchForm();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onCloseClick(): void {
        const ref = this._dialog.open<
            CloseGoalModalCommitmentComponent,
            any,
            CloseGoalCommitmentOption
        >(CloseGoalModalCommitmentComponent);
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret == null) {
                        return;
                    }

                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 5000,
                        placement: 'bottom-right',
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/restrict-template-expressions
                        data: {
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            text: `${this._translateService.instant(
                                TranslationKeyMap._9524_THE_GOAL_HAS_BEEN_CLOSED,
                            )} - ${ret}`,
                        } as CmpsTextOnlySnackBarOptions,
                    });
                    const goal = this._form.form.value as GoalFormValue;
                    const closeGoalValue: CommitmentCloseGoalValue = {
                        closeOption: ret,
                        commitmentKpi: this.commitmentKpis.find(
                            (e) => e.goalId === goal.goalId,
                        ),
                    };
                    this.onGoalCloseClick.emit(closeGoalValue);

                    /// /// separator ////// consolidate during refactor //////

                    const metric =
                        this.state.metrics.find(
                            (a) => a.metricId === goal.metric,
                        ) ||
                        // TODO revise. this is related to existing goal metrics that are not part of the performer's current coachable metrics
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        ({ metricName: goal.metricName } as CoachableMetric);
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const nextGoal = {
                        ...goal,
                        metricName: metric.metricName,
                        statusId: GoalStatus.PENDING_CLOSE,
                    } as GoalFormValue;
                    // trigger a new draft-update to be added
                    if (nextGoal.isOriginalGoal) {
                        nextGoal.isOriginalGoal = undefined;
                        nextGoal.diffItemId = undefined;
                        nextGoal.tracking_id = Math.random();
                    }
                    this.onSaveGoalClick.emit(nextGoal);
                    this._selected = { ...this._selected, ...nextGoal };

                    /// /// separator ////// consolidate during refactor //////

                    useDispatcher(this, goalCommitmentRetrieveDataCancel({}));
                    this._patchForm();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _reloadData(metricId: number): void {
        this.isLoadingMetricPerformanceData = true;
        this._graphSet = null;
        useDispatcher(this, goalCommitmentRetrieveDataCancel({}));
        const now = new Date();

        this.startInitGrid = true;
        this._initGrid();

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const periodtype =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._form && this._form.form
                ? this._localPeriodType
                : PeriodType.DAY;

        const opts = {
            viewType: PerformanceDataViewType.USER_SUPER_GROUP,
            periodType: periodtype,
            date: now,
            startDate: addDays(now, -90),
            userId: this.performerId,
            goalId: this.goal?.goalId,
            metricId,
            displayNameCriteriaIdType: CriteriaIdType.USER_ID,
            outputCriteriaIdType: CriteriaIdType.USER_ID,
        };
        const optsSG = {
            ...opts,
            viewType: PerformanceDataViewType.SUPER_GROUP,
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const opts30Day = { ...opts, periodType: PeriodType.ROLLING_30 };

        const sgobs =
            optsSG.periodType === PeriodType.ROLLING_30
                ? this._teamOAService.retrieveCoachingPerformanceData(
                      GraphType.TREND,
                      optsSG,
                  )
                : this._teamOAService.retrievePerformanceData(
                      GraphType.TREND,
                      optsSG,
                  );

        sgobs.subscribe((a) => {
            if (this._selected && a) {
                const sgdata = a;
                this.sgData = sgdata || [];
            }

            useDispatcher(
                this,
                goalCommitmentRetrieveData({
                    graphType: GraphType.TREND,
                    opts,
                }),
            );
        });
    }

    private _patchForm(): void {
        if (this._form) {
            this.calculatedGoalOrImprovement = false;
            this._form.clearWarningsErrors();
            this._form.form.reset(this._selected, { emitEvent: false });

            // eslint-disable-next-line @typescript-eslint/dot-notation
            if (
                // eslint-disable-next-line @typescript-eslint/dot-notation
                !this._form.form.controls['improvement'].value &&
                this._selected.goal != null
            ) {
                this._patchFormDataDependentLazy();
            }

            this._setDisabledState(this.disabled);
        }

        this._resetMetricOpts();
    }

    private _patchFormDataDependentLazy(): void {
        let c: AbstractControl;
        let valueFn;

        // improvement gets precedence
        const improvement = this._form.form.value.improvement;
        if (improvement != null && !isNaN(improvement)) {
            c = this._form.form.controls[this._form.fields.GOAL.name];
            valueFn = () =>
                this._calcGoalFromImprovement(
                    this._form.form.value.improvement,
                );
        } else {
            c = this._form.form.controls[this._form.fields.IMPROVEMENT.name];
            valueFn = () =>
                this._calcImprovementFromGoal(this._form.form.value.goal);
        }

        // for lazy patching control must already be dirty
        // if (c.dirty) { // or not
        this._patchFormDataDependent(c, valueFn);

        this.calculatedGoalOrImprovement = true;
        // }
    }

    private _sharedGoalModalCreate(): Observable<boolean> {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9545_SHARED_GOALS,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9546_THIS_PERFORMER_ALREADY_HAS_A_GOAL_WITH_THIS_METRIC_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9547_REVIEW,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        return ref.result.pipe(map((ret) => ret === 0));
    }

    private _patchFormDataDependent(
        c: AbstractControl,
        valueFn: () => number | string,
    ): void {
        c.markAsDirty();
        c.updateValueAndValidity({ emitEvent: false });
        /* if (!this.state.lastMetricDataItem) {
            return;
        } */
        c.setValue(valueFn(), { emitEvent: false });

        // re-cache calculated values through user interaction
        if (this._selected && this._form.form && this._form.form.value) {
            this._selected = {
                ...this._selected,
                goal: this._form.form.value.goal,
            };
        }
    }

    private _calcGoalFromImprovement(value: number): number | string {
        if (this._selected) {
            const actualtouse =
                this._selected.actual != null
                    ? this._selected.actual
                    : this._selected.initialActual;
            if (actualtouse == null) {
                return this._selected.goal;
            }
            return (
                (value / 100 / this._selected.rankOrder) * actualtouse +
                actualtouse
            ).toFixed(2);
        }
    }

    private _calcImprovementFromGoal(goalvalue: number): number | string {
        if (this._selected) {
            const actualtouse =
                this._selected.actual != null
                    ? this._selected.actual
                    : this._selected.initialActual;
            if (actualtouse == null) {
                return 0;
            }
            return (
                ((goalvalue - actualtouse) / actualtouse) *
                this._selected.rankOrder *
                100
            ).toFixed(2);
        }
        return null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get startPercentToGoal() {
        if (this._selected) {
            const actualtouse = this._selected.actual
                ? this._selected.actual
                : this._selected.initialActual;
            const variance =
                ((actualtouse - this._selected.goal) / this._selected.goal) *
                this._selected.rankOrder;
            return ((variance + 1) * 100).toFixed(2);
        }
        return null;
    }

    // Left side panel
    // TODO eventually move this into state
    private _setDaysToAchievement(value: Date): void {
        const estimatedCompletionDate = new Date(value);
        estimatedCompletionDate.setHours(0, 0, 0, 0);

        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);

        const gThresholds = (
            this.goalThresholds ? [...this.goalThresholds] : []
        ).sort((a, b) => a.threshold - b.threshold);

        this._daysToAchievement = determineDaysToAchievement(
            estimatedCompletionDate,
            startDate,
            estimatedCompletionDate,
            this._selected.statusId,
            gThresholds,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get saveButtonText() {
        if (this._reviewingSharedGoal) {
            return this._translateService.instant(
                TranslationKeyMap._9450_ASSOCIATE_GOAL,
            );
        } else if (!this._selected.goalId) {
            return this._translateService.instant(
                TranslationKeyMap._9451_SET_NEW_GOAL,
            );
        } else if (this._selected.goalId && this._isEditing) {
            return this._translateService.instant(
                TranslationKeyMap._9452_SET_UPDATED_GOAL,
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setGoalCreator(createdBy: string) {
        this.goalCreator = createdBy;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setOppActiveDays(activeDays: number) {
        this.oppActiveDays = activeDays;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    open_popup(e: MouseEvent) {
        const ref = this._dialog.open(SharedGoalsCommitmentComponent, {
            data: {
                selectedGoalId: this.selectedGoalId,
                goalsActives: this.goalsActives,
            },
        });
        ref.result.pipe(takeUntilDestroy(this)).subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    show_snackbar() {
        this.selectedGoalId = this.goal;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.selectedGoalId && this.selectedGoalId.shared) {
            this.goalsActives = this.goalsFromOtherCommitments.filter(
                (item) =>
                    item.statusId === GoalStatus.ACTIVE &&
                    item.goalId === this.selectedGoalId.goalId &&
                    item.commitmentId !== this.selectedGoalId.commitmentId,
            );
            if (this.goalsActives.length > 0) {
                this.isEnableSnackbar = true;
                const group = [];
                this.goalsActives.forEach((element) => {
                    const validate = group.some(
                        (item) => item.commitmentId === element.commitmentId,
                    );
                    if (!validate) {
                        group.push({
                            commitmentId: element.commitmentId,
                            activeGoalDays: element.activeGoalDays,
                            actual: element.actual,
                            behaviourName: element.behaviourName,
                            createdBy: element.createdBy,
                            createdOn: element.createdOn,
                            dateSet: element.dateSet,
                            daysActive: element.daysActive,
                            daysToGoalExpiry: element.daysToGoalExpiry,
                            estimatedCompletionDate:
                                element.estimatedCompletionDate,
                            eventId: element.eventId,
                            goalId: element.goalId,
                            improvementRaring: element.improvementRaring,
                            improvementSinceLastGoal:
                                element.improvementSinceLastGoal,
                            inicialActual: element.inicialActual,
                            initialTarget: element.initialTarget,
                            oppActiveDays: element.oppActiveDays,
                            overallImprovement: element.overallImprovement,
                            percentToGoal: element.percentToGoal,
                            percentToTarget: element.percentToTarget,
                            periodTypeId: element.periodTypeId,
                            shared: element.shared,
                            statusId: element.statusId,
                            statusName: element.statusName,
                            subBehaviourName: element.subBehaviourName,
                            target: element.target,
                            updatedOn: element.updatedOn,
                        });
                    }
                });
                const metrics = [];
                for (let i = 0; i < group.length; i++) {
                    const metric = [];
                    for (
                        let j = 0;
                        j < this.goalsFromOtherCommitments.length;
                        j++
                    ) {
                        if (
                            group[i].commitmentId ===
                                this.goalsFromOtherCommitments[j]
                                    .commitmentId &&
                            this.goalsFromOtherCommitments[j].statusId ===
                                GoalStatus.ACTIVE
                        ) {
                            metric.push(
                                this.goalsFromOtherCommitments[j].metricName,
                            );
                        }
                    }
                    metrics.push({
                        commitmentId: group[i].commitmentId,
                        metricName: metric.join(';'),
                        activeGoalDays: group[i].activeGoalDays,
                        actual: group[i].actual,
                        behaviourName: group[i].behaviourName,
                        createdBy: group[i].createdBy,
                        createdOn: group[i].createdOn,
                        dateSet: group[i].dateSet,
                        daysActive: group[i].daysActive,
                        daysToGoalExpiry: group[i].daysToGoalExpiry,
                        estimatedCompletionDate:
                            group[i].estimatedCompletionDate,
                        eventId: group[i].eventId,
                        goalId: group[i].goalId,
                        improvementRaring: group[i].improvementRaring,
                        improvementSinceLastGoal:
                            group[i].improvementSinceLastGoal,
                        inicialActual: group[i].inicialActual,
                        initialTarget: group[i].initialTarget,
                        oppActiveDays: group[i].oppActiveDays,
                        overallImprovement: group[i].overallImprovement,
                        percentToGoal: group[i].percentToGoal,
                        percentToTarget: group[i].percentToTarget,
                        periodTypeId: group[i].periodTypeId,
                        shared: group[i].shared,
                        statusId: group[i].statusId,
                        statusName: group[i].statusName,
                        subBehaviourName: group[i].subBehaviourName,
                        target: group[i].target,
                        updatedOn: group[i].updatedOn,
                    });
                }
                this.goalsActives = metrics;
            } else {
                this.isEnableSnackbar = false;
            }
        } else {
            this.isEnableSnackbar = false;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _resetChart() {
        this.startInitTrend = true;

        this.actualTrendData = [
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            {
                mainData: [...generateEmptyStateData()],
                goalData: [],
                targetData: [],
                sgAvgData: [],
                eventData: [],
            } as RawGraphData,
        ];
        this.actualVarianceData = [];
        this.metricnames = [];

        this.showEmptyGraph = true;

        // this._resetGrid();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _resetGrid() {
        this.actualGridData = [{}, {}, {}];
        this.gridPeriods = null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _initchart() {
        // switch (this.activeSubTab) {
        // case GraphType.TREND:
        this._resetChart();

        if (!this.state.metricData || !this.state.metricData.metrics) return;

        this.startInitTrend = true;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const trenddata = {
            mainData: this.state.metricData
                ? this.state.metricData.metrics
                : [],
            sgAvgData: this.sgData,
            showSg: false,
        } as RawGraphData;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._selected &&
            this._selected.goalId &&
            this.state.goalHistory
        ) {
            trenddata.goalData = [...this.state.goalHistory];
            // modify the FIRST (very latest goal event item)
            this.applyGoalValueAndExpectedDateChange(trenddata.goalData);
        } else {
            trenddata.goalData = [...this.fillDraftGoalChartData()];
        }

        if (
            this._selected &&
            (this._selected.metric || this._selected.goalId) &&
            this.state.goalEventDataPoints
        ) {
            trenddata.eventData = [...this.state.goalEventDataPoints];
        } else {
            trenddata.eventData = [];
        }
        // this.actualTrendData.forEach(f => {
        //     f.showSg = false;
        //     return f;
        // });

        this.actualTrendData = [];

        this.showEmptyGraph = false;

        this.actualTrendData = [...this.actualTrendData, trenddata];
        // break;
        // case GraphType.VARIANCE:
        this.startInitVariance = true;
        this.actualVarianceData = [];
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const vardata = {
            mainData: this.state.metricData
                ? this.state.metricData.metrics
                : [],
            sgAvgData: [],
            showSg: false,
        } as RawGraphData;
        this.actualVarianceData = [...this.actualVarianceData, vardata];
    }

    applyGoalValueAndExpectedDateChange(
        datapoints: CommitmentGoalHistory[],
    ): void {
        const goalvalue =
            this._form.form.controls[this._form.fields.GOAL.name].value;
        const expdate =
            this._form.form.controls[this._form.fields.DATE.name].value;

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const appendableDatapoint = {
            ...datapoints[0],
            date: new Date(),
        } as CommitmentGoalHistory;

        let updated = false;

        if (appendableDatapoint.goalValue !== goalvalue) {
            appendableDatapoint.goalValue = goalvalue;
            updated = true;
        }
        if (
            !datesAreEqual(appendableDatapoint.expectedAchievementDate, expdate)
        ) {
            appendableDatapoint.expectedAchievementDate = expdate;
            updated = true;
        }

        updated && datapoints.unshift(appendableDatapoint);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _initGrid() {
        // set grid data:
        // this.startInitGrid = true;
        this._resetGrid();

        // TODO consolidate with pillar-three
        this._piService
            .listCacheDates({
                periodTypeIds: [this._localPeriodType],
            })
            .subscribe((g: CacheDateItem[]) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                const topmtd = g && g.length ? g[0] : null;
                this.topdate = topmtd ? topmtd.endDate : CoachingDateToday;
                this.generateGridDataRequest(this.topdate);
            });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    fillDraftGoalChartData() {
        const goalvalue =
            this._form.form.controls[this._form.fields.GOAL.name].value;
        const expdate =
            this._form.form.controls[this._form.fields.DATE.name].value;

        if (goalvalue == null) return [];

        return [
            {
                goalValue:
                    this._selected.actual || this._selected.initialActual || 0,
                expectedAchievementDate: new Date(
                    new Date().setHours(0, 0, 0, 0),
                ),
            },
            {
                goalValue: Number(goalvalue),
                expectedAchievementDate: expdate,
            },
        ];
    }

    // TODO consolidate with pillar-three
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    set30DayDates(today) {
        return [
            {
                endDate: today,
                startDate: subDays(today, 30),
            },
            {
                endDate: subDays(today, 31),
                startDate: subDays(today, 60),
            },
            {
                endDate: subDays(today, 61),
                startDate: subDays(today, 90),
            },
        ];
    }

    // TODO consolidate with pillar-three
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setMonthDates(today) {
        const mEnd = endOfMonth(today);
        const mStart = startOfMonth(today);

        return [
            {
                endDate: mEnd,
                startDate: mStart,
            }, // end date of this month
            {
                endDate: endOfMonth(subMonths(mStart, 1)),
                startDate: startOfMonth(subMonths(mStart, 1)),
            }, // end date of last month
            {
                endDate: endOfMonth(subMonths(mStart, 2)),
                startDate: startOfMonth(subMonths(mStart, 2)),
            },
        ];
    }

    // TODO consolidate with pillar-three
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setWeekDates(today) {
        const wEnd = endOfWeek(today);
        const wStart = startOfWeek(today);

        return [
            {
                endDate: wEnd,
                startDate: wStart,
            },
            {
                endDate: subWeeks(wEnd, 1),
                startDate: subWeeks(wStart, 1),
            },
            {
                endDate: subWeeks(wEnd, 2),
                startDate: subWeeks(wStart, 2),
            },
        ];
    }

    // TODO consolidate with pillar-three
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setQuarterDates(today) {
        const qtEnd = endOfQuarter(today);
        const qtStart = startOfQuarter(today);

        return [
            {
                endDate: qtEnd,
                startDate: qtStart,
            },
            {
                endDate: subQuarters(qtEnd, 1),
                startDate: subQuarters(qtStart, 1),
            },
            {
                endDate: subQuarters(qtEnd, 2),
                startDate: subQuarters(qtStart, 2),
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    generateGridDataRequest(today) {
        const dates = [];
        today = new Date(today);

        switch (this._localPeriodType) {
            case PeriodType.DAY:
            case PeriodType.ROLLING_30:
                dates.push(...this.set30DayDates(today));
                break;

            case PeriodType.MONTH:
                dates.push(...this.setMonthDates(today));
                break;

            case PeriodType.WEEK:
                dates.push(...this.setWeekDates(today));
                break;

            default:
                dates.push(...this.setQuarterDates(today));
                break;
        }

        this.gridPeriods = dates;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const tempdata = [];

        const periodicData: MetricDetailGridObservables[] = [];
        const metricid = this.goal
            ? this.goal.metric
            : this._form.form.value.metric;
        let metric = this.state.metrics
            ? this.state.metrics.find((a) => a.metricId === metricid)
            : null;

        if (!metric) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            metric = { metricId: metricid } as CoachableMetric;
        }

        this.actualGridData = [];

        if (metric.metricId) {
            dates.forEach((date, idx) => {
                const opts = {
                    viewType: PerformanceDataViewType.USER_SUPER_GROUP,
                    periodType: this._localPeriodType,
                    date: date.endDate, // get the end date for each period
                    userId: this.performerId,
                    metricId: metric.metricId,
                };

                const metricrequests = [];
                this.metricnames.push(metric.metricName);
                metricrequests.push(this.pullGridData(opts));

                // const components = this.activeMetric.coachedComponents;
                // this.componentLength = 0;

                // if (components && components.length) {
                //     components.forEach(c => {
                //         if (c.opportunityType === OpportunityType.ACTIVE
                //             || c.opportunityType === OpportunityType.CLOSED_STILL_OPP
                //             || c.opportunityType === OpportunityType.CLOSED_NOT_OPP) {
                //             opts.metricId = [c.metricId];
                //             this.componentLength++;
                //             this.metricnames.push(c.metric.metricShortName);
                //             metricrequests.push(this.pullGridData(opts));
                //         }
                //     });
                // }

                periodicData.push({
                    period: date,
                    observables: metricrequests,
                });
            });

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const cursor = 0;

            const periodicObservables: Array<Observable<PerformanceData[]>> =
                [];

            periodicData.forEach((p) => {
                periodicObservables.push(...p.observables);
            });

            if (periodicData.length && periodicData[0].observables) {
                this.getPeriods(periodicObservables);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getPeriods(periodicObservables) {
        const sub = forkJoin(periodicObservables).subscribe(
            (d) => {
                if (!d) return;

                if (sub) {
                    sub.unsubscribe();
                }

                const periodPerf: PerformanceData[] = [];

                d.forEach((e: PerformanceData[]) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (e && e.length) {
                        const f = e.length - 1;
                        periodPerf.push(e[f]); // get last
                    } else {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        periodPerf.push({} as PerformanceData);
                    }
                });

                this.actualGridData = periodPerf;
            },
            () => {
                this.actualGridData = null;
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    generateGraphDataRequest(today, withSGAvg = true, component?: Opportunity) {
        const lastyear = addDays(today, -90);
        const metricId = this.goal.metric;

        if (!today || !lastyear) return;

        const opts = {
            viewType: PerformanceDataViewType.USER_SUPER_GROUP,
            periodType: PeriodType.DAY, // this._localPeriodType
            date: today, // get the end date for each period
            startDate: lastyear,
            userId: this.performerId,
            metricId,
        };

        if (this.activeSubTab === GraphType.TREND) {
            this.startInitVariance = false;
        } else if (this.activeSubTab === GraphType.VARIANCE) {
            this.startInitTrend = false;
        }

        const req = this.pullGraphData(opts, component ? true : null);
        if (withSGAvg) {
            const opts2 = {
                ...opts,
                viewType: PerformanceDataViewType.SUPER_GROUP,
            };
            const req2 = this.pullGraphData(opts2, component ? true : null);

            this.graphRequest([req, req2], component);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    pullGraphData(opts: any, hideSpinner = null) {
        if (opts.periodType === PeriodType.ROLLING_30) {
            return this._teamOAService.retrieveCoachingPerformanceData(
                this.activeSubTab,
                opts,
            );
        } else {
            return this._teamOAService.retrievePerformanceData(
                this.activeSubTab,
                opts,
            );
        }
    }

    pullGridData(opts): Observable<any> {
        return this._teamOAService.retrievePerformanceData(
            this.activeSubTab,
            opts,
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    graphRequest(requests, component) {
        const sub = forkJoin(requests).subscribe(
            (data: any) => {
                if (!data) return;

                if (this.activeSubTab === GraphType.TREND) {
                    this.startInitTrend = true;

                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const newdata = {
                        mainData: data[0],
                        sgAvgData: data[1],
                        showSg: false,
                        component: component || undefined,
                    } as RawGraphData;

                    if (this._selected.goalId && this.state.goalHistory) {
                        newdata.goalData = [...this.state.goalHistory];
                    } else {
                        newdata.goalData = [];
                    }

                    if (
                        this._selected.goalId &&
                        this.state.goalEventDataPoints
                    ) {
                        newdata.eventData = [...this.state.goalEventDataPoints];
                    } else {
                        newdata.eventData = [];
                    }

                    this.actualTrendData.forEach((f) => {
                        f.showSg = false;
                        return f;
                    });

                    this.actualTrendData = [...this.actualTrendData, newdata];
                } else {
                    this.startInitVariance = true;
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const newdata = {
                        mainData: data[0],
                        sgAvgData: data[1],
                        showSg: false,
                    } as RawGraphData;
                    this.actualVarianceData = [
                        ...this.actualVarianceData,
                        newdata,
                    ];
                }
            },
            () => {
                // error
                if (this.activeSubTab === GraphType.TREND) {
                    this.actualTrendData = [];
                }
                if (this.activeSubTab === GraphType.VARIANCE) {
                    this.actualVarianceData = [];
                }
            },
            () => {
                // complete
                if (sub) sub.unsubscribe();
            },
        );
    }

    get goalReviewTitle(): string {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this._selected && this._selected.goalId) {
            if (this._isEditing) {
                return this._translateService.instant(
                    TranslationKeyMap._9449_GOAL_UPDATE,
                );
            } else {
                let title = this._translateService.instant(
                    TranslationKeyMap._9448_GOAL_REVIEW,
                );
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                title +=
                    this.isGoalFromOtherCommitment &&
                    this._selected.statusId !== GoalStatus.CLOSED
                        ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                          ` (${this._translateService.instant(
                              TranslationKeyMap._9453_FROM_ANOTHER_COMMITMENT_,
                          )})`
                        : '';
                return title;
            }
        } else {
            return this._translateService.instant(
                TranslationKeyMap._9436_GOAL_CREATOR,
            );
        }
    }

    private _resetMetricOpts(): void {
        if (!this._metricOpts) {
            return;
        }
        // eslint-disable-next-line no-return-assign
        this._metricOpts.forEach(
            (e) =>
                (e.disabled = this.commitmentKpis.some(
                    (ee) =>
                        ee.metricId === e.value &&
                        !(
                            ee.statusId === GoalStatus.CLOSED ||
                            ee.statusId === GoalStatus.DELETED
                        ),
                )),
        );
        this._metricOpts = [...this._metricOpts];
    }

    // TODO this would be the method to be used when this component is refactored to implement ControlValueAccessor
    private _setDisabledState(isDisabled: boolean): void {
        const value = this._form.form.value as GoalFormValue;
        isDisabled || value.associateToParentFocus
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });
    }

    private _dateIsValid(value: Date): boolean {
        const compareDate = this._value.goalId
            ? this._selected.date
            : new Date();
        return +startOfDay(value) >= +startOfDay(compareDate);
    }
}
